import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { type OriginCheckResult, ScanResultType } from "~/features/scanDmc/types/ScanDMC";

enum FlagCodes {
  GREEN = "GREEN", // Everything okay
  RED = "RED", // Error from Origin Check, check flagType for more info
  YELLOW = "YELLOW",
  BLUE = "BLUE",
}

const COUNTERFEIT_FLAGS = ["F", "T", "A", "A2", "I"];
const UNKNOWN_FLAGS = ["U"];

type CounterfeitFlags = "F" | "T" | "A" | "A2" | "I";
type UnknownFlags = "U";

type FlagTypes = CounterfeitFlags | UnknownFlags | "C" | "X";

export interface DMCCodeOriginCheckRequestDTO {
  code: string;
}

export interface DMCCodeOriginCheckResponseDTO {
  wasScannedBefore?: boolean;
  codeFlag: FlagCodes;
  flagType?: FlagTypes | null;
  isValidCode: boolean;
  // Unused - FE manually checks if the code is from outer packaging
  isOverPackaging?: boolean;
  // Unused
  logoUrl?: string;
  productCode?: string;
}

export async function fetchScanDMCOriginCheck(dmcCode: string, force?: boolean): Promise<OriginCheckResult> {
  const apiUrl = occUrl(`/dmc/scandmcapi?forceApiCall=${!!force}`);

  const response = await occFetch(apiUrl, {
    body: JSON.stringify({ code: dmcCode } satisfies DMCCodeOriginCheckRequestDTO),
    method: "POST",
    cache: "no-cache",
    headers: {
      "content-type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error("Error fetching data");
  }
  const data = (await response.json()) as DMCCodeOriginCheckResponseDTO;

  let resultType: ScanResultType;
  // isValidCode means only that the format is valid, not that the code is valid
  if (data.isValidCode && data.codeFlag !== FlagCodes.RED) {
    if (data.codeFlag === FlagCodes.YELLOW) {
      resultType = ScanResultType.Suspicious;
    } else if (data.wasScannedBefore) {
      resultType = ScanResultType.AlreadyScanned;
    } else {
      resultType = ScanResultType.Success;
    }
    return {
      type: resultType,
      productCode: data.productCode,
      dmcCode,
    };
  } else {
    if (data.flagType && COUNTERFEIT_FLAGS.includes(data.flagType)) {
      resultType = ScanResultType.Counterfeit;
    } else if ((data.flagType && UNKNOWN_FLAGS.includes(data.flagType)) || !data.isValidCode) {
      resultType = ScanResultType.UnknownCode;
    } else {
      resultType = ScanResultType.UnknownError;
    }
    return {
      type: resultType,
      dmcCode,
    };
  }
}
